// The look each chatter had last (7TV paint and badge, StreamNook cosmetics,
// third-party badges, personal emotes), kept in localStorage. It does two jobs:
//   - a chatter seen in an earlier session shows that look at once, while the
//     fresh answer is asked for in the background (a row only changes if they
//     switched cosmetics since), instead of every first row waiting for it;
//   - while 7TV is down, a chatter still wears their last 7TV paint and badge.
// The app keeps the same per device (seventv_worn_cosmetics.json): a real
// answer is kept, a failed lookup is answered from it.
//
// A paint is a few KB, so each one is kept once and shared by everyone wearing
// it, and the whole store stays small: the page's storage also holds the last
// good emote lists, which matter more. Every read and write is guarded.

const KEY = 'sn_7tv_worn_v1';
/** Chatters kept; the least recently seen go first. */
export const MAX_WORN_CHATTERS = 2_000;
/** Paints kept, the ones the most recently seen chatters wear. */
export const MAX_WORN_PAINTS = 100;
/** The store never grows past this many characters. */
export const MAX_WORN_CHARS = 600_000;
/** Writes wait this long, so a busy chat writes once in a while, not per row. */
export const WORN_SAVE_AFTER_MS = 30_000;

/** A chatter's identity answer as the overlay keeps it. */
export type Look = Record<string, unknown> & { paint?: unknown | null };

interface Chatter {
  paintId: string | null;
  /** The answer without its paint (and without one-off flags). */
  rest: Record<string, unknown>;
}

interface Stored {
  /** chatter key -> [paint id, the rest of the look] */
  c: [string, string | null, Record<string, unknown>][];
  /** paint id -> paint */
  p: [string, unknown][];
}

/** Flags about one answer, never part of a look. */
const TRANSIENT = new Set(['seventv_unavailable', 'paint']);

let chatters: Map<string, Chatter> | null = null;
let paints = new Map<string, unknown>();
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function loaded(): Map<string, Chatter> {
  if (chatters) return chatters;
  chatters = new Map();
  paints = new Map();
  try {
    const raw = storage()?.getItem(KEY);
    const stored = raw ? (JSON.parse(raw) as Stored) : null;
    for (const p of Array.isArray(stored?.p) ? stored!.p : []) {
      if (Array.isArray(p) && typeof p[0] === 'string' && p[1] && typeof p[1] === 'object') paints.set(p[0], p[1]);
    }
    for (const c of Array.isArray(stored?.c) ? stored!.c : []) {
      if (!Array.isArray(c) || typeof c[0] !== 'string' || !c[2] || typeof c[2] !== 'object') continue;
      const paintId = typeof c[1] === 'string' && paints.has(c[1]) ? c[1] : null;
      chatters.set(c[0], { paintId, rest: c[2] });
    }
  } catch {
    /* a broken store starts empty */
  }
  return chatters;
}

function paintIdOf(paint: unknown): string | null {
  const id = paint && typeof paint === 'object' ? (paint as { id?: unknown }).id : undefined;
  return typeof id === 'string' && id ? id : null;
}

/** What `key` looked like last, or null when nothing is kept. */
export function lookFor(key: string): Look | null {
  const c = loaded().get(key);
  if (!c) return null;
  return { ...c.rest, paint: c.paintId ? (paints.get(c.paintId) ?? null) : null };
}

/** The look to show a chatter at once: the kept one, unless one is already
 *  drawn (that one stays until the fresh answer replaces it). */
export function lookStandIn(key: string, drawn: boolean): Look | null {
  return drawn ? null : lookFor(key);
}

/** Keep a real answer (one 7TV answered; a failed lookup is never kept). */
export function rememberLook(key: string, answer: object): void {
  const look = answer as Look;
  if (!key || !look || look.seventv_unavailable) return;
  const map = loaded();
  const paintId = paintIdOf(look.paint);
  if (paintId) paints.set(paintId, look.paint);
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(look)) if (!TRANSIENT.has(k)) rest[k] = v;
  map.delete(key);
  map.set(key, { paintId, rest });
  if (map.size > MAX_WORN_CHATTERS) map.delete(map.keys().next().value as string);
  scheduleSave();
}

function scheduleSave(): void {
  if (saveTimer) return;
  saveTimer = setTimeout(saveNow, WORN_SAVE_AFTER_MS);
}

/** Write the store now (also on page hide, so a reload keeps the last answers). */
export function saveNow(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  const map = chatters;
  const ls = storage();
  if (!map || !ls) return;
  // Newest first: the paints the most recent chatters wear are the ones kept.
  const newest = [...map.entries()].reverse();
  const keptPaints = new Map<string, unknown>();
  const rows: Stored['c'] = [];
  for (const [key, c] of newest) {
    let paintId = c.paintId;
    if (paintId && !keptPaints.has(paintId)) {
      const paint = paints.get(paintId);
      if (paint && keptPaints.size < MAX_WORN_PAINTS) keptPaints.set(paintId, paint);
      else paintId = null;
    }
    rows.push([key, paintId, c.rest]);
  }
  paints = new Map([...paints].filter(([id]) => keptPaints.has(id)));
  let raw = '';
  try {
    rows.reverse();
    raw = JSON.stringify({ c: rows, p: [...keptPaints] } satisfies Stored);
    // Too big: the oldest chatters go until it fits (their paints with them).
    while (raw.length > MAX_WORN_CHARS && rows.length > 0) {
      rows.splice(0, Math.max(1, Math.ceil(rows.length / 10)));
      const used = new Set(rows.map((r) => r[1]).filter((id): id is string => !!id));
      raw = JSON.stringify({ c: rows, p: [...keptPaints].filter(([id]) => used.has(id)) } satisfies Stored);
    }
  } catch {
    return;
  }
  try {
    ls.setItem(KEY, raw);
  } catch {
    // Full: give up this store rather than anyone else's.
    try {
      ls.removeItem(KEY);
    } catch {
      /* nothing kept; the overlay still works */
    }
  }
}

if (typeof window !== 'undefined') {
  try {
    window.addEventListener('pagehide', saveNow);
  } catch {
    /* no window events (tests) */
  }
}
